
import React, { useEffect, useState } from "react";
import { Grid, CircularProgress, Box } from "@mui/material";
import { useNavigate } from "react-router-dom";
import {
  callAPI,
  IsSmallScreen,
  Method,
  updateMainHeader,
} from "../../commonFunctions/CommonFunctions";
import PermissionDenied from "../../commonFunctions/PermissionDenied";
import { AllPermissions } from "../../commonFunctions/PermissionManger";
import WSDataGrid, { IColumns } from "../../Components/DataGrid/WSDataGrid";
import WSMobileDataGridAdvanced, {
  IOrderColumns,
} from "../../Components/DataGrid/WSMobileDataGridAdvanced";
import { breadCrumbsStore } from "../../Components/BreadCrumbs/BreadCrumbsListner";
import { useSnackbar } from "../../Components/Snackbar/SnackBarContext";
import ConfirmDialog, { useConfirmDialog } from "../../Components/Confirmation/ConfirmDialog";

interface IPurchaseOrder {
  billID: string;
  vendorID: string;
  vendorName?: string;
  billNo: string;
  billDate: string;
  dueDate: string;
  totalAmount: number;
  status: string;
}

export const PurchaseOrderList: React.FC<any> = React.memo(() => {
  const { add, edit, remove, view } = AllPermissions.getRolePermission;
  const { showMessage } = useSnackbar();
  const navigate = useNavigate();
  const isMobile = IsSmallScreen();

  const [orders, setOrders] = useState<IPurchaseOrder[]>([]);
  const [loading, setLoading] = useState(false);

  const {
    open,
    title,
    onConfirm,
    handleClose,
    openConfirmDialog,
  } = useConfirmDialog();

  useEffect(() => {
    updateMainHeader("Purchase Orders");
    breadCrumbsStore.updateBreadCrumbs([{ "Purchase Orders": "" }]);
    fetchAllOrders();
  }, []);

  const fetchAllOrders = async () => {
    setLoading(true);
    try {
      const response: any = await callAPI("PurchaseOrder", Method.GET);
      const orderList: IPurchaseOrder[] = Array.isArray(response)
        ? response
        : Array.isArray(response?.data)
        ? response.data
        : [];
      setOrders(orderList.map((order) => ({ ...order, id: order.billID })));
    } catch (err) {
      console.error(err);
      showMessage("Error fetching Purchase Orders", "error");
      setOrders([]);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: string) => {
    try {
      const response: any = await callAPI(`PurchaseOrder`, Method.DELETE, null, id);

      if (!response || response?.success === true) {
        showMessage("Purchase Order deleted successfully", "success");
        fetchAllOrders();
      } else {
        showMessage(response?.message || "Error deleting Purchase Order", "error");
      }
    } catch (err) {
      console.error(err);
      showMessage("Error deleting Purchase Order", "error");
    } finally {
      handleClose(); // Close dialog after deletion
    }
  };

  const handleAction = (action: string, row: IPurchaseOrder) => {
    const orderId = row.billID;
    if (action === "Edit") {
      navigate(`/purchase/edit/${orderId}`);
    } else if (action === "Delete" && remove) {
      openConfirmDialog("Delete this Purchase Order?", () => handleDelete(orderId));
    }
  };

  const columns: IColumns[] = [
  { header: "Bill No", fieldName: "billNo", type: "default" },
  { header: "Vendor Name", fieldName: "vendorName", type: "default" }, // Changed
  { header: "Bill Date", fieldName: "billDate", type: "date" },
  { header: "Due Date", fieldName: "dueDate", type: "date" },
  { header: "Total Amount", fieldName: "totalAmount", type: "default" },
  { header: "Status", fieldName: "status", type: "default" },
  {
    header: "Actions",
    fieldName: "actions",
    type: "action",
    actions: [edit ? "Edit" : undefined, remove ? "Delete" : undefined].filter(Boolean) as string[],
    actionCallback: handleAction,
  },
];

const mobColumns: IOrderColumns[] = [
  { header: "Bill No", fieldName: "billNo", type: "default" },
  { header: "Vendor Name", fieldName: "vendorName", type: "default" }, // Changed
  { header: "Total Amount", fieldName: "totalAmount", type: "default" },
  {
    header: "Actions",
    fieldName: "actions",
    type: "action",
    actions: [edit ? "Edit" : undefined, remove ? "Delete" : undefined].filter(Boolean) as string[],
    actionCallback: handleAction,
  },
];


  const handleClickAdd = () => navigate("/purchase/add");

  return (
    <>
      <Grid container spacing={2}>
        {view ? (
          <Grid item xs={12}>
            {loading ? (
              <Box display="flex" justifyContent="center" alignItems="center" height="60vh">
                <CircularProgress />
              </Box>
            ) : !isMobile ? (
              <WSDataGrid
                id="purchaseOrderListGrid"
                title="Purchase Orders"
                columns={columns}
                source={{
                  filter: [],
                  page: 10,
                  pageSize: 1000,
                  sort: [],
                  url: "PurchaseOrder/search",
                }}
                searchBox={{ fieldName: "billNo" }}
                addButton={add ? { label: "New", color: "secondary" } : undefined}
                onAddButtonClick={handleClickAdd}
              />
            ) : (
              <WSMobileDataGridAdvanced
                title="Purchase Orders"
                id="purchaseOrderListGrid"
                columns={mobColumns}
                data={orders}
                addButton={add ? { label: "New", color: "secondary" } : undefined}
                onAddButtonClick={handleClickAdd}
              />
            )}
          </Grid>
        ) : (
          <PermissionDenied />
        )}
      </Grid>

      {/* ✅ Confirmation Dialog */}
      <ConfirmDialog
        open={open}
        title={title}
        onConfirm={onConfirm}
        onClose={handleClose}
      />
    </>
  );
});
